/**
 * 3-bucket chip variant resolver — Multi-Add Phase 2 (PRD §2.4 + §2.11).
 *
 * Per `reference-rules-in-comments` (ai.knowledge/global). The variant
 * scheme:
 *
 *   - `fully-configured`     — no chip (component returns null)
 *   - `needs-attention`      — red, alert-circle (auth INVALID on either)
 *   - `added-with-warnings`  — yellow, alert-triangle (video VALIDATED
 *     but control INVALID/UNREACHABLE — the device added but one
 *     aspect is degraded)
 *   - `failed`               — red, close-circle (no state-sync entry;
 *     the failed-tile placeholder over `failedDevices` slice)
 *
 * Per `think-in-systems` (ai.knowledge/global): the resolver is a pure
 * function of `(videoAuthStatus, controlAuthStatus)` — the chip
 * component is a pure render of the resolved variant. This split lets
 * the 9-combo table-driven test cover the matrix without rendering a
 * single React component.
 *
 * Subscribers MUST read the auth-status fields via a `useDepot`
 * selector (e.g.
 * `useDepot((d) => d.external.transients[deviceId]?.videoAuthenticationStatus)`)
 * — NOT a captured prop. The resolver runs against whatever values the
 * selector returns at re-render time, so state-sync transitions land
 * immediately. This module is just the pure function; the selector
 * pattern is the consumer's responsibility (`item.tsx` already follows
 * it via `useChipState`).
 *
 * Per `state-sync-optimistic-mutations.md`
 * (ai.knowledge/platform/studio.client): local mutations to depot state
 * land before the authority roundtrip — chips re-render on the next
 * tick without waiting for the authority confirmation.
 */
import { AuthenticationStatus } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { T_Maybe } from '@tryiris-ai/js-types';

/**
 * Discriminated chip variant.
 *
 *   fully-configured     — no chip rendered (healthy device)
 *   needs-attention      — red, auth INVALID
 *   added-with-warnings  — yellow, partial-success
 *   failed               — red, no state-sync entry
 *   select-model         — neutral/blue, control-only discovery
 *                          (ONVIF row with no video aspect) per §2.9.
 *                          Prompts the user to pick a model so the
 *                          bridge can build the URX before the device
 *                          is added. NOT a terminal-state failure;
 *                          it's the click-to-configure surface for the
 *                          pre-add wizard. Render a NEW chip variant
 *                          rather than co-opting one of the state
 *                          buckets (Select model is a UX affordance,
 *                          not a degraded state).
 */
export type ChipVariant =
  | 'fully-configured'
  | 'needs-attention'
  | 'added-with-warnings'
  | 'failed'
  | 'select-model';

/**
 * Resolve the chip variant from the two auth-status signals.
 *
 * The terminal-only contract is the same as Phase 1 (see chip-state.ts
 * priority cascade) — transient statuses (UNKNOWN / VALIDATING) and
 * missing values fall to `fully-configured` so the chip does not flash
 * during the post-add settle window.
 *
 * Mapping rules:
 *
 *   videoAuth=INVALID                    → needs-attention
 *   controlAuth=INVALID + videoAuth=VAL  → added-with-warnings
 *   both INVALID                          → needs-attention (auth dominates)
 *   both VALIDATED                        → fully-configured
 *   otherwise (transient / unknown)       → fully-configured
 *
 * `failed` is NOT derived here — failed devices live in a separate slice
 * (`source.failedDevices`) because they never committed to state-sync.
 * The failed-tile renderer constructs `variant: 'failed'` directly.
 *
 * @param videoAuth  - per-aspect video authentication status (from depot
 *   transients or state-sync proxy)
 * @param controlAuth - per-aspect control authentication status
 * @returns the 4-bucket variant
 */
export const deriveChipVariant = (
  videoAuth: T_Maybe<AuthenticationStatus>,
  controlAuth: T_Maybe<AuthenticationStatus>
): ChipVariant => {
  const VALIDATED = AuthenticationStatus.AUTHENTICATION_STATUS_VALIDATED;
  const INVALID = AuthenticationStatus.AUTHENTICATION_STATUS_INVALID;

  // Priority 1 — video auth INVALID. The video aspect is the primary
  // signal: a tile with bad video creds cannot stream at all, so this
  // outranks the control-only warning regardless of control state.
  if (videoAuth === INVALID) {
    return 'needs-attention';
  }

  // Priority 2 — video VALIDATED but control INVALID. The tile renders
  // (it can stream), but PTZ + settings will fail until the user
  // refreshes credentials. Yellow / "added with warnings" matches the
  // partial-success outcome.
  if (videoAuth === VALIDATED && controlAuth === INVALID) {
    return 'added-with-warnings';
  }

  // Priority 3 — both VALIDATED. No chip.
  if (videoAuth === VALIDATED && controlAuth === VALIDATED) {
    return 'fully-configured';
  }

  // Default — transient (UNKNOWN / VALIDATING) or one side missing.
  // No chip flashes during the post-add settle window. This is the
  // same terminal-only rule as Phase 1's chip-state.ts cascade.
  return 'fully-configured';
};

/**
 * Map a `failedDevices.slice` reason → secondary chip copy for the
 * failed-tile placeholder. Failed tile renders a primary "Failed" chip
 * + secondary discriminated copy.
 *
 * Strings ship as reasonable defaults per `code-comments-required` —
 * locked by Product / Design at QA time. Candidate strings:
 *   - "Authentication failed"
 *   - "Device locked — try again in 30s"
 *   - "Linked to another studio"
 * TODO(Product): lock final copy.
 */
export const failedReasonCopy = (
  reason:
    | 'auth'
    | 'locked'
    | 'already-linked'
    | 'commit-rejected'
    | 'bridge-disambiguation'
    | 'error'
): string => {
  switch (reason) {
    case 'auth':
      return 'Authentication failed';
    case 'locked':
      return 'Device locked — try again in 30s';
    case 'already-linked':
      return 'Linked to another studio';
    case 'bridge-disambiguation':
      return 'Bridge not resolved';
    case 'commit-rejected':
      return 'Could not connect';
    case 'error':
    default:
      return 'Failed to add';
  }
};
